import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SandboxCard from "@/components/SandboxCard";
import { joinWaitlist } from "@/lib/api";
import { scrollToSection } from "@/components/Reveal";

const HeadlineLine = ({ children, index }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const yOrb = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);

    const submit = async (e) => {
        e.preventDefault();
        if (loading) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error("Enter a valid email to join the waitlist");
            return;
        }
        setLoading(true);
        try {
            const res = await joinWaitlist(email.trim(), "early builder");
            toast.success(`You're #${res.position} on the waitlist`);
            setEmail("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong — try again");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="top" ref={heroRef} className="relative min-h-screen overflow-hidden pt-32 pb-16 lg:pt-36">
            <div className="dot-grid absolute inset-0 opacity-70" />
            <motion.div style={{ y: yOrb }} className="pointer-events-none absolute inset-0">
                <div className="absolute -top-32 left-[8%] h-[420px] w-[420px] rounded-full bg-neon-cyan/15 blur-[140px]" />
                <div className="absolute bottom-[-10%] right-[5%] h-[460px] w-[460px] rounded-full bg-neon-violet/20 blur-[150px]" />
                <div className="absolute top-[40%] left-[45%] h-[300px] w-[300px] rounded-full bg-neon-emerald/10 blur-[120px]" />
            </motion.div>
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-neon-cyan/40 to-transparent" />

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2"
                    >
                        <ShieldCheck size={13} className="text-neon-emerald" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-300 sm:text-[11px]">
                            Trust infrastructure for AI agents + proof-of-humanity
                        </span>
                    </motion.div>

                    <h1 className="font-display text-[2.1rem] font-extrabold leading-[1.08] tracking-tight text-white sm:text-6xl lg:text-[3.4rem] xl:text-[3.9rem]">
                        <HeadlineLine index={0}>Identity for humans.</HeadlineLine>
                        <HeadlineLine index={1}>
                            <span className="text-gradient">Execution proofs</span>
                        </HeadlineLine>
                        <HeadlineLine index={2}>for AI agents.</HeadlineLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.75 }}
                        className="mt-6 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base"
                    >
                        Authena binds every AI agent to a verified human with a chain-agnostic SUID, then
                        cryptographically receipts everything it does — private, provable, and ready for SOC2,
                        GDPR and MiCA review.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.9 }}
                        className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center"
                    >
                        <form onSubmit={submit} className="flex w-full max-w-md gap-2" data-testid="hero-waitlist-form">
                            <input
                                type="email"
                                data-testid="hero-waitlist-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@protocol.xyz"
                                className="h-12 flex-1 rounded-lg border border-white/10 bg-white/[0.04] px-4 font-mono text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-neon-cyan/50"
                            />
                            <button
                                type="submit"
                                data-testid="hero-waitlist-submit"
                                disabled={loading}
                                className="inline-flex h-12 items-center gap-2 whitespace-nowrap rounded-lg bg-neon-cyan px-5 font-mono text-xs font-bold uppercase tracking-[0.14em] text-void transition-all duration-300 hover:shadow-[0_0_32px_rgba(0,240,255,0.45)] disabled:opacity-60"
                            >
                                {loading ? "Joining…" : "Join waitlist"}
                                <ArrowRight size={14} />
                            </button>
                        </form>
                        <a
                            href="#how"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("#how");
                            }}
                            data-testid="hero-demo-link"
                            className="inline-flex h-12 items-center justify-center rounded-lg border border-white/10 px-6 font-mono text-xs uppercase tracking-[0.14em] text-slate-300 transition-colors hover:border-neon-cyan/40 hover:text-neon-cyan"
                        >
                            How it works
                        </a>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-7 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500"
                    >
                        ZK proof-of-humanity · Chain-agnostic SUID · Edge verified &lt;12ms
                    </motion.p>
                </div>

                <motion.div
                    style={{ y: yCard }}
                    initial={{ opacity: 0, y: 48, rotateX: 8 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    transition={{ duration: 1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-5"
                >
                    <SandboxCard />
                </motion.div>
            </div>

            <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex">
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-600">Scroll</span>
                <span className="scroll-cue block h-10 w-px bg-gradient-to-b from-neon-cyan/70 to-transparent" />
            </div>
        </section>
    );
}